import React from 'react'
import cx from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import { Button } from '~elements'
import { ButtonProps, ResponsiveImage } from '~types'
import { NEXT_IMAGE_QUALITY } from '~constants'

import styles from './CarouselCard.module.scss'

export type StatusTagType = 'just_announced' | 'presale_now' | 'sphere_origins' | 'premium' | 'added_shows'

export const StatusTagLabels: Record<StatusTagType, string> = {
  just_announced: 'Just Announced',
  presale_now: 'Presale Now',
  sphere_origins: 'Sphere Origins',
  premium: 'Premium',
  added_shows: 'Added Shows'
}

export interface StatusTagProps {
  type: StatusTagType
  text: string
}

export interface CarouselCardProps {
  responsiveImage: ResponsiveImage
  primaryButton?: ButtonProps
  secondaryButton?: ButtonProps
  statusTag?: StatusTagType
  className?: string
  imageHref: string
  tabIndex?: number
  // Used for sorting cards in the carousel
  tagTextOverride?: string
  isChildOfHeroCarousel?: boolean
}

const StatusTag = ({ type, text }: StatusTagProps) => {
  return <div className={cx(styles.statusTag, styles[`statusTag--${type}`])}>{text}</div>
}

const CarouselCard = ({
  responsiveImage,
  primaryButton,
  secondaryButton,
  statusTag,
  className,
  imageHref,
  tabIndex,
  tagTextOverride,
  isChildOfHeroCarousel = false
}: CarouselCardProps) => {
  // Destructure responsive multimedia fields
  const { imageSm, imageMd, imageXl } = responsiveImage ?? {}
  const fallbackImage = imageXl
  const fallbackAlt = imageXl?.altText ?? ''
  // Analytics
  const linkModule = 'carousel'
  const linkLocation = isChildOfHeroCarousel ? 'hero_carousel' : ''

  return (
    <div className={cx(styles.carouselCard, className)}>
      <div className={styles.imageContainer}>
        <Link href={imageHref} tabIndex={tabIndex} aria-label={`View details for ${fallbackAlt || 'event'}`}>
          <picture>
            {imageSm?.url && <source srcSet={imageSm.url} media='(max-width: 767px)' />}
            {imageMd?.url && <source srcSet={imageMd.url} media='(max-width: 1440px)' />}
            {fallbackImage?.url && (
              <Image
                style={{}}
                src={fallbackImage.url}
                alt={fallbackAlt}
                fill
                quality={NEXT_IMAGE_QUALITY}
                className={styles.backgroundImage}
              />
            )}
          </picture>
        </Link>
      </div>
      {statusTag && (
        <div className={styles.statusTagContainer}>
          <StatusTag type={statusTag} text={tagTextOverride || StatusTagLabels[statusTag]} />
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.buttonContainer}>
          {primaryButton && (
            <Button
              {...primaryButton}
              analytics={{
                linkLocation,
                linkModule,
                linkPosition: '1'
              }}
            />
          )}
          {secondaryButton && (
            <Button
              {...secondaryButton}
              analytics={{
                linkLocation,
                linkModule,
                linkPosition: '2'
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default CarouselCard
export { StatusTag }
